import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Flex, SimpleGrid, Spacer } from '@chakra-ui/react';
import { StatusBadge } from '@shinetools/invoicing-renderer';

import { InvoiceType } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import { InvoiceFromList } from 'common/hooks/useGetInvoicesQuery';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import { formatDate } from 'helpers/date';

import { QuoteFromList } from '../../pages/hooks/useGetQuotes';
import EditMenu from '../EditMenu';
import { MenuItem } from '../EditMenu/types';
import locales from './locales';

export type InvoiceOrQuote = InvoiceFromList | QuoteFromList;
interface WorkListItemProps {
  item: InvoiceOrQuote;
  menuItems: MenuItem[];
}

/**
 * List item used in invoices, quotes lists
 */
const WorkListItem: FC<WorkListItemProps> = ({ item, menuItems }) => {
  const id = 'invoiceId' in item ? item.invoiceId : item.quoteId;

  const isInvoice = item.type === InvoiceType.Invoice;
  const pathname = item.type === InvoiceType.Invoice ? 'invoices' : 'quotes';

  const description = item.fiscalId
    ? locales.formatString(locales.withFiscalId.description(isInvoice), {
        createdAt: formatDate(new Date(item.startAt), 'P'),
        fiscalId: item.fiscalId,
      })
    : locales.formatString(locales.inProgress.description(isInvoice), {
        createdAt: formatDate(new Date(item.createdAt), 'P'),
        fiscalId: item.fiscalId,
      });

  return (
    <SunshineCard
      alignItems="center"
      as={Link}
      flexDirection="row"
      to={`/work/${pathname}/${id}`}
    >
      <SimpleGrid
        alignItems="center"
        columns={3}
        paddingRight="space-24"
        spacing="space-24"
        width="100%"
      >
        <SunshineCard.Content>
          <SunshineCard.Title fontWeight="normal">
            {item.title} · {item.client?.name}
          </SunshineCard.Title>

          <SunshineCard.Text size="sm">{description}</SunshineCard.Text>
        </SunshineCard.Content>

        <SimpleGrid columns={2}>
          <Spacer />

          <Flex justifyContent="flex-start">
            <StatusBadge
              flexShrink={0}
              size="sm"
              status={item.status}
              type={item.type}
            />
          </Flex>
        </SimpleGrid>

        <Text color="grey.800" fontWeight="weight-500" textAlign="right">
          {formatAmount({
            forcePlusSign: false,
            value: item.totalAmountAfterTaxes,
          })}
        </Text>
      </SimpleGrid>

      <SunshineCard.Slot name="action">
        <EditMenu menuItems={menuItems} />
      </SunshineCard.Slot>
    </SunshineCard>
  );
};

export default WorkListItem;
